/**
 * src/lib/api/taskPhotos.js — TICKET-765: ADDITIONAL images on one task.
 *
 * A task anchors to one photo (tasks.photo_document_id + tasks.region). Extra
 * images of the same work item live in task_photos, each with its own optional
 * region. There is no dual truth: the anchor is always the first image, the
 * task_photos rows are the rest — `taskPhotoList()` is the single place that
 * composes the two, so no consumer reimplements the ordering.
 *
 * Soft-archive only (archived_at) — never hard-delete (src/CLAUDE.md).
 * Returns raw snake_case rows. See docs/SHAPE_CONTRACT.md.
 */

import { supabase } from '../supabase';
import { logEvent } from '../observability';

/**
 * One spine row per failed WRITE, then the same {success:false} the caller already
 * handles. A silent write failure here reads to the user as "the photo just did
 * not attach", with nothing to look at afterwards.
 */
function fail(errorType, step, err, metadata = {}) {
  const message = err?.message || String(err || 'unknown');
  // spine-emit-ok: logEvent is fire-and-forget by contract and never throws — the
  // caller's job is to surface the returned error, not to await telemetry.
  logEvent(errorType, message, { module: 'task_photos', step, status: 'error', error_message: message, ...metadata });
  return { success: false, error: message };
}

const SELECT_TASK_PHOTO = 'id, task_id, photo_document_id, region, caption, sort_order, created_at';

/**
 * Compose "every image of this task": the anchor photo first (from the task row
 * itself), then the extra task_photos in sort_order. Pure — no I/O — so the
 * ordering rule is unit-testable and shared by every surface that renders a
 * task's images (markup panel, service order, quote crops).
 *
 * @param {object} task — a tasks row (photo_document_id + region)
 * @param {Array} extras — task_photos rows for that task
 * @returns {Array<{photo_document_id, region, caption, is_anchor, task_photo_id}>}
 */
export function taskPhotoList(task, extras = []) {
  const out = [];
  if (task?.photo_document_id) {
    out.push({
      photo_document_id: task.photo_document_id,
      region: task.region ?? null,
      caption: null,
      is_anchor: true,
      task_photo_id: null,
    });
  }
  [...extras]
    .filter((r) => !r.archived_at)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .forEach((r) => out.push({
      photo_document_id: r.photo_document_id,
      region: r.region ?? null,
      caption: r.caption ?? null,
      is_anchor: false,
      task_photo_id: r.id,
    }));
  return out;
}

/** The extra images of ONE task (anchor excluded — compose with taskPhotoList). */
export async function fetchTaskPhotos(taskId) {
  if (!taskId) return [];
  try {
    const { data, error } = await supabase
      .from('task_photos')
      .select(SELECT_TASK_PHOTO)
      .eq('task_id', taskId)
      .is('archived_at', null)
      .order('sort_order', { ascending: true });
    if (error) { console.warn('[taskPhotos] fetch error:', error.message); return []; }
    return data || [];
  } catch (err) {
    // spine-emit-ok: best-effort read; warns + returns [] so callers degrade
    console.warn('[taskPhotos] fetchTaskPhotos failed:', err.message);
    return [];
  }
}

/** The extra images of MANY tasks at once, keyed by task_id (list surfaces). */
export async function fetchTaskPhotosFor(taskIds = []) {
  const ids = [...new Set(taskIds.filter(Boolean))];
  if (ids.length === 0) return {};
  try {
    const { data, error } = await supabase
      .from('task_photos')
      .select(SELECT_TASK_PHOTO)
      .in('task_id', ids)
      .is('archived_at', null)
      .order('sort_order', { ascending: true });
    if (error) { console.warn('[taskPhotos] fetchFor error:', error.message); return {}; }
    return (data || []).reduce((acc, r) => {
      (acc[r.task_id] = acc[r.task_id] || []).push(r);
      return acc;
    }, {});
  } catch (err) {
    // spine-emit-ok: best-effort read; warns + returns {} so callers degrade
    console.warn('[taskPhotos] fetchTaskPhotosFor failed:', err.message);
    return {};
  }
}

/**
 * Attach another image to a task. Appends at the end (sort_order = current
 * count). The UNIQUE (task_id, photo_document_id) makes a re-attach a no-op
 * rather than a duplicate — surfaced as `already_attached`, not an error dialog.
 */
export async function addTaskPhoto({ task_id, photo_document_id, region = null, caption = null }) {
  if (!task_id || !photo_document_id) return { success: false, error: 'task_id and photo_document_id required' };
  try {
    const existing = await fetchTaskPhotos(task_id);
    const { data, error } = await supabase
      .from('task_photos')
      .insert({ task_id, photo_document_id, region, caption, sort_order: existing.length })
      .select(SELECT_TASK_PHOTO)
      .single();
    if (error) {
      if (error.code === '23505') return { success: true, already_attached: true };
      return fail('task_photo_add', 'insert', error, { task_id, photo_document_id });
    }
    return { success: true, record: data };
  } catch (err) {
    const message = err?.message || String(err);
    logEvent('task_photo_add', message, { module: 'task_photos', step: 'insert', status: 'error', error_message: message, ...{ task_id, photo_document_id } });
    return { success: false, error: message };
  }
}

/** Edit an extra image's region/caption (the anchor is patched via updateTask). */
export async function updateTaskPhoto(id, patch = {}) {
  if (!id) return { success: false, error: 'id required' };
  const updates = {};
  if (patch.region !== undefined) updates.region = patch.region;
  if (patch.caption !== undefined) updates.caption = patch.caption || null;
  if (patch.sort_order !== undefined) updates.sort_order = patch.sort_order;
  if (Object.keys(updates).length === 0) return { success: true };
  try {
    const { data, error } = await supabase
      .from('task_photos')
      .update(updates)
      .eq('id', id)
      .select(SELECT_TASK_PHOTO)
      .single();
    if (error) return fail('task_photo_update', 'update', error, { id });
    return { success: true, record: data };
  } catch (err) {
    const message = err?.message || String(err);
    logEvent('task_photo_update', message, { module: 'task_photos', step: 'update', status: 'error', error_message: message, ...{ id } });
    return { success: false, error: message };
  }
}

/** Detach an extra image — SOFT archive, never a delete (src/CLAUDE.md). */
export async function removeTaskPhoto(id) {
  if (!id) return { success: false, error: 'id required' };
  try {
    const { error } = await supabase
      .from('task_photos')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', id);
    if (error) return fail('task_photo_remove', 'archive', error, { id });
    return { success: true };
  } catch (err) {
    const message = err?.message || String(err);
    logEvent('task_photo_remove', message, { module: 'task_photos', step: 'archive', status: 'error', error_message: message, ...{ id } });
    return { success: false, error: message };
  }
}
